import React from "react";
import { Check, ChevronRight } from "lucide-react";

/**
 * ListRow — issue #750 § 5.9. A white card-radius row (grounds, players,
 * invites…): a name, an optional figures line, an optional tag chip, and
 * an optional trailing selection control. `Squad.dc.html:122,130,138` draws
 * a `1px solid #E2E8F0` border on the row (without it the row is
 * white-on-white on a white card) and puts the tag chip inside the text
 * column, below the figures line, `align-self: flex-start`
 * (`Squad.dc.html:126`) — not as a trailing sibling.
 *
 * `selected`: `null` renders no trailing control, `true` a navy tick,
 * `false` an empty ring — the three states the drawings actually use
 * (no control, chosen, choosable-but-not-chosen).
 *
 * `chevron` (issue #866 review round 1, A1/R1) — opt-in, `false` by
 * default. `DirectionA1.dc.html:84-95` draws a 20px `#94A3B8` chevron on
 * the SAVED/PLAYED rows only — the drawing has no "results found" state
 * for near-me or search at all (only their empty/failure states:
 * `near-me-failed`, `near-me-nothing-near`, `search-no-match`, none of
 * which draws a ground row), so it never actually specifies what a
 * populated near-me or search row looks like. `LogMatchGroundStep.jsx`'s
 * three call sites all pass `chevron` anyway — near-me and search get it
 * as a BUILD CHOICE, for consistency with the one row kind the drawing
 * does specify, not because the drawing shows it there too. The
 * style-clobber fix restored the card but not this glyph, which no
 * conformance tool can see (`copy-check`/`structure-check`/`token-check`
 * all read the artboard's own named elements or its declared colour
 * palette — a decorative SVG the drawing draws but never names is outside
 * all three). Copies `DisclosureRow.jsx`'s own chevron exactly (`size={20}`,
 * `color: "var(--nw-ink-faint)"`, `aria-hidden`) — no `marginLeft` needed
 * there because THIS row already gives its children a 12px `gap` (below),
 * where `DisclosureRow`'s `justify-content: space-between` button does not.
 * Mutually exclusive with `selected` in every drawn instance — a caller
 * passing both is a drawing question, not one this component resolves.
 *
 * KEYBOARD/ROLE (issue #866 review round 1, A1) — a row with an `onClick`
 * used to be a `<div>`: clickable by mouse, invisible to a screen reader
 * (`<div>` carries no default role) and unreachable by keyboard (a `<div>`
 * is never in the tab order, so Tab could not land on it and Enter/Space
 * had nothing to activate). `onClick` is now destructured out instead of
 * riding along in `...rest`, so it can also be wired to `role="button"`,
 * `tabIndex={0}` and an Enter/Space `onKeyDown` — the same three additions
 * `button`-izing a clickable non-button element always needs, applied only
 * when a caller actually passes `onClick` (a read-only row, e.g. Squad's
 * once it ships, stays a plain, non-interactive `<div>`). None of this
 * changes a single CSS property — the row's own `style`/`className` are
 * untouched, so its look is identical to before.
 *
 * `chip` (issue #1319) — an optional second chip beside `tag`, for a row
 * the board marks twice (a coach's game in Coming up). Absent, the row's markup
 * is exactly what it was.
 *
 * `style` MERGES onto the kit style above rather than replacing it (issue
 * #866). A caller only ever needs to add one property — `LogMatchGroundStep.
 * jsx`'s three rows add `cursor: "pointer"` — but `{...rest}` used to spread
 * a caller's whole `style` object over this component's own AFTER it was
 * set, so that one property replaced the card surface, border, radius and
 * padding wholesale (`DirectionA1.dc.html:84-95`'s white card / `#E2E8F0`
 * border / 12px radius / 56px min-height, gone — the fix filed as G3).
 * `style` is destructured out here so it can be spread INTO the object
 * below instead, the same pattern `ActionButton`'s own `...style` uses —
 * the caller still wins on any property it names, it just no longer
 * erases the ones it doesn't.
 *
 * `minHeight: 56` / `padding: "12px 16px"` are `DirectionA1.dc.html:84-95`'s
 * own drawn values for this row — the only screen ListRow ships on today
 * (`LogMatchGroundStep.jsx`). `Squad.dc.html`'s own rows (not yet built)
 * draw `12px 14px` with no explicit min-height; when that screen ships,
 * follow the kit's own "genuinely different numbers get their own token"
 * rule rather than force one value on both.
 */
export function ListRow({
  name,
  figuresLine = null,
  tag = null,
  chip = null,
  selected = null,
  refusal = null,
  chevron = false,
  className = "",
  "data-testid": testId,
  style,
  onClick,
  onKeyDown,
  ...rest
}) {
  const interactive = typeof onClick === "function";
  const handleKeyDown = (event) => {
    if (interactive && (event.key === "Enter" || event.key === " " || event.key === "Spacebar")) {
      event.preventDefault();
      onClick(event);
    }
    if (typeof onKeyDown === "function") onKeyDown(event);
  };
  const tagSpan = tag ? (
    <span
      style={{
        alignSelf: "flex-start",
        background: "var(--nw-navy-tint)",
        color: "var(--nw-navy)",
        fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
        fontSize: 11,
        fontWeight: 600,
        borderRadius: "var(--nw-radius-pill)",
        padding: "2px 8px",
      }}
    >
      {tag}
    </span>
  ) : null;
  return (
    <div
      data-testid={testId}
      className={`flex items-center ${className}`}
      style={{
        background: "var(--nw-surface)",
        border: "1px solid var(--nw-line)",
        borderRadius: "var(--nw-radius-card)",
        minHeight: 56,
        padding: "12px 16px",
        gap: 12,
        ...style,
      }}
      onClick={onClick}
      onKeyDown={interactive ? handleKeyDown : onKeyDown}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      {...rest}
    >
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
        <div className="nw-rowname" style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
          {name}
        </div>
        {figuresLine && <div className="nw-help">{figuresLine}</div>}
        {refusal && <div className="nw-help">{refusal}</div>}
        {/* `Squad.dc.html:122,126` draws the tag chip INSIDE this text
            column, below the figures line, `align-self: flex-start` — not
            as a trailing sibling beside the tick. */}
        {chip ? (
          // Issue #1319: "Match" and "Scheduled by your coach", side by side.
          <div style={{ alignSelf: "flex-start", display: "flex", flexWrap: "wrap", gap: 6 }}>
            {tagSpan}
            <span
              data-testid="list-row-chip"
              style={{
                border: "1px solid var(--nw-navy)",
                color: "var(--nw-navy)",
                fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                fontSize: 11,
                fontWeight: 600,
                borderRadius: "var(--nw-radius-pill)",
                padding: "1px 8px",
              }}
            >
              {chip}
            </span>
          </div>
        ) : (
          tagSpan
        )}
      </div>
      {selected === true && (
        <Check size={18} style={{ color: "var(--nw-navy)", flexShrink: 0 }} aria-hidden="true" />
      )}
      {selected === false && (
        <span
          aria-hidden="true"
          style={{
            width: 18,
            height: 18,
            borderRadius: "var(--nw-radius-pill)",
            border: "2px solid var(--nw-line-strong)",
            flexShrink: 0,
          }}
        />
      )}
      {chevron && (
        <ChevronRight size={20} style={{ color: "var(--nw-ink-faint)", flexShrink: 0 }} aria-hidden="true" />
      )}
    </div>
  );
}

export default ListRow;
